import React, { useEffect, useState, useMemo } from "react";
import { Input, Select, Modal } from "antd";
import {
  CheckOutlined,
  WarningOutlined,
  UndoOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { GetTasksByUnitTreeApi } from "../../api/tasks/GetTasksByUnitTreeApi";
import GetPositionsPerformanceApi from "../../api/individuals/GetPositionsPerformanceApi";
import CompleteTaskApi from "../../api/tasks/CompleteTaskApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";
import moment from "moment";

const { Option } = Select;

// ── CSS for PendingButton — injected once at module level ─────────────────────
const PENDING_BTN_STYLE = `
  .pending-btn {
    position: relative; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    padding: 8px 12px; border-radius: 6px; cursor: pointer;
    background: #f8fafc; border: 1px solid #e2e8f0;
    font-size: 12px; font-weight: 600; color: #64748b;
    transition: background 0.3s ease, border-color 0.3s ease, color 0.3s ease;
    user-select: none;
  }
  .pending-btn .label-default { transition: opacity 0.35s ease, transform 0.35s ease; }
  .pending-btn .label-hover   { position: absolute; opacity: 0; transform: translateY(4px); transition: opacity 0.35s ease, transform 0.35s ease; color: #065f46; display: flex; align-items: center; gap: 5px; }
  .pending-btn:hover { background: #f0fdf4; border-color: #6ee7b7; }
  .pending-btn:hover .label-default { opacity: 0; transform: translateY(-4px); }
  .pending-btn:hover .label-hover   { opacity: 1; transform: translateY(0); }
`;

function PendingButton({ onConfirm }) {
  return (
    <div className="pending-btn" onClick={onConfirm}>
      <span className="label-default">Not completed</span>
      <span className="label-hover">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 6.5L4.5 9L10 3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Mark completed
      </span>
    </div>
  );
}

export default function WeeklyReflection() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null); // null = show all weeks
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [positions, setPositions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null); // keyed by userId, not positionId
  const [selectedUnit, setSelectedUnit] = useState(null); // unit name filter
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchPositions();
      fetchAllTasks();
    }
  }, [positionState, perfCycleState]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAllTasks() {
    setLoading(true);
    const response = await GetTasksByUnitTreeApi(perfCycleState.id, positionState.id);
    if (response.success) setTasks(response.data || []);
    setLoading(false);
  }

  async function fetchPositions() {
    setPositionsLoading(true);
    const response = await GetPositionsPerformanceApi(perfCycleState.id, 1, positionState.id, 100);
    if (response.message === "success") setPositions(response.data?.positions || []);
    setPositionsLoading(false);
  }

  const weekOptions = useMemo(() => {
    const options = [];
    const totalWeeks = moment().year(selectedYear).isoWeeksInYear();
    for (let w = 1; w <= totalWeeks; w++) {
      const start = moment().year(selectedYear).isoWeek(w).startOf("isoWeek").format("DD MMM");
      const end = moment().year(selectedYear).isoWeek(w).endOf("isoWeek").format("DD MMM");
      options.push({ value: w, label: `W${w} (${start} – ${end})` });
    }
    return options;
  }, [selectedYear]);

  const yearOptions = useMemo(() => {
    const current = moment().year();
    return [current - 1, current, current + 1];
  }, []);

  // Deduplicate positions by userId — each user gets one entry with ALL their position IDs
  // Only include users with a name who have at least one task in the loaded task list
  const userOptions = useMemo(() => {
    // Build a set of all position IDs that appear on tasks
    const activePositionIds = new Set();
    tasks.forEach((t) => {
      if (t.assignedTo) activePositionIds.add(t.assignedTo);
      if (t.plan?.assignedTo) activePositionIds.add(t.plan.assignedTo);
    });

    const map = new Map(); // userId → { userId, name, imageUrl, positionIds[] }
    positions.forEach((pos) => {
      const userId = pos.users?.id || pos.userId;
      if (!userId) return;
      const name = pos.users
        ? `${pos.users.firstName || ""} ${pos.users.lastName || ""}`.trim()
        : "";
      if (!name) return; // skip blank / unnamed entries
      if (!map.has(userId)) {
        map.set(userId, {
          userId,
          name,
          imageUrl: pos.users?.imageUrl || null,
          positionIds: [],
        });
      }
      map.get(userId).positionIds.push(pos.id);
    });

    return [...map.values()]
      .filter((u) => u.positionIds.some((pid) => activePositionIds.has(pid))) // only users with tasks
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [positions, tasks]);

  // Unit options for the unit filter
  const unitOptions = useMemo(() => {
    const unitSet = new Set();
    positions.forEach(pos => {
      if (pos.units?.name) unitSet.add(pos.units.name);
    });
    return [...unitSet].sort();
  }, [positions]);

  // All position IDs for the selected user (used in filters)
  const selectedPositionIds = useMemo(() => {
    if (!selectedUserId) return null;
    return userOptions.find((u) => u.userId === selectedUserId)?.positionIds || null;
  }, [selectedUserId, userOptions]);

  // Determine which weeks have active tasks (for highlighting in the dropdown)
  const weeksWithTasks = useMemo(() => {
    const activeWeeks = new Set();
    let filtered = tasks;
    if (selectedPositionIds) {
      filtered = filtered.filter((task) =>
        selectedPositionIds.includes(task.assignedTo) ||
        selectedPositionIds.includes(task.plan?.assignedTo)
      );
    }
    filtered.forEach(task => {
      const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
      const taskStart = task.taskStartDate ? moment(task.taskStartDate) : null;
      if (taskStart && taskEnd) {
        // Mark all weeks between start and end
        const startWeek = taskStart.year() === selectedYear ? taskStart.isoWeek() : 1;
        const endWeek = taskEnd.year() === selectedYear ? taskEnd.isoWeek() : moment().year(selectedYear).isoWeeksInYear();
        if (taskStart.year() <= selectedYear && taskEnd.year() >= selectedYear) {
          for (let w = Math.max(1, startWeek); w <= Math.min(moment().year(selectedYear).isoWeeksInYear(), endWeek); w++) {
            activeWeeks.add(w);
          }
        }
      } else if (taskEnd && taskEnd.year() === selectedYear) {
        activeWeeks.add(taskEnd.isoWeek());
      } else if (taskStart && taskStart.year() === selectedYear) {
        activeWeeks.add(taskStart.isoWeek());
      }
    });
    return activeWeeks;
  }, [tasks, selectedYear, selectedPositionIds]);

  const weekTasks = useMemo(() => {
    // Apply user filter first
    let filtered = tasks;
    if (selectedPositionIds) {
      filtered = filtered.filter((task) =>
        selectedPositionIds.includes(task.assignedTo) ||
        selectedPositionIds.includes(task.plan?.assignedTo)
      );
    }

    // Apply unit filter
    if (selectedUnit) {
      const unitPositionIds = positions.filter(p => p.units?.name === selectedUnit).map(p => p.id);
      filtered = filtered.filter((task) =>
        unitPositionIds.includes(task.assignedTo) ||
        unitPositionIds.includes(task.plan?.assignedTo)
      );
    }

    // If no week selected, show ALL tasks for this year (from active weeks only)
    if (!selectedWeek) {
      return filtered.filter((task) => {
        const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
        const taskStart = task.taskStartDate ? moment(task.taskStartDate) : null;
        // Show tasks that touch the selected year
        if (taskEnd && taskEnd.year() === selectedYear) return true;
        if (taskStart && taskStart.year() === selectedYear) return true;
        if (taskStart && taskEnd && taskStart.year() <= selectedYear && taskEnd.year() >= selectedYear) return true;
        return false;
      });
    }

    // Week is selected — filter to that specific week
    const start = moment().year(selectedYear).isoWeek(selectedWeek).startOf("isoWeek");
    const end = moment().year(selectedYear).isoWeek(selectedWeek).endOf("isoWeek");
    return filtered.filter((task) => {
      const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
      const taskStart = task.taskStartDate ? moment(task.taskStartDate) : null;
      if (taskStart && taskEnd) return taskStart.isSameOrBefore(end) && taskEnd.isSameOrAfter(start);
      if (taskEnd) return taskEnd.isSameOrAfter(start) && taskEnd.isSameOrBefore(end);
      if (taskStart) return taskStart.isSameOrBefore(end) && taskStart.isSameOrAfter(start);
      return false;
    });
  }, [tasks, selectedWeek, selectedYear, selectedPositionIds, selectedUnit, positions]);

  // Sort: on hold → pending → completed
  const sortedTasks = useMemo(() => {
    return [...weekTasks].sort((a, b) => {
      const getOrder = (t) => {
        if (t.onHold) return 0;
        if (!t.completionStatus) return 1;
        return 2;
      };
      return getOrder(a) - getOrder(b);
    });
  }, [weekTasks]);

  const completedCount = weekTasks.filter((t) => t.completionStatus).length;

  async function handleComplete(taskId) {
    const apiResponse = await CompleteTaskApi(taskId);
    if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Task marked as completed");
      fetchAllTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't complete task");
    }
  }

  function handleStatusChange(taskId, status) {
    setStatuses((prev) => ({ ...prev, [taskId]: status }));
  }

  function handleUndo(taskId) {
    setStatuses((prev) => {
      const copy = { ...prev };
      delete copy[taskId];
      return copy;
    });
  }

  if (loading) return <PageLoader />;

  return (
    <div style={{ maxWidth: 1100 }}>
      <style>{PENDING_BTN_STYLE}</style>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: "-0.3px" }}>
            Weekly Reflection
          </h2>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
            {selectedWeek
              ? `Week W${selectedWeek} (${moment().year(selectedYear).isoWeek(selectedWeek).startOf("isoWeek").format("DD MMM")} – ${moment().year(selectedYear).isoWeek(selectedWeek).endOf("isoWeek").format("DD MMM")}) · `
              : `All weeks ${selectedYear} · `
            }
            {userOptions.length > 1 ? "All users" : (positionState?.users?.firstName || "User")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Year filter */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", borderRadius: 8, border: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
            <Select value={selectedYear} onChange={(val) => { setSelectedYear(val); const max = moment().year(val).isoWeeksInYear(); if (selectedWeek > max) setSelectedWeek(max); }} size="small" bordered={false} style={{ width: 70 }} dropdownStyle={{ fontSize: 12 }}>
              {yearOptions.map((y) => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </div>
          {/* Week filter */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", borderRadius: 8, border: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
            <Select
              value={selectedWeek}
              onChange={(val) => setSelectedWeek(val ?? null)}
              size="small" bordered={false} style={{ width: 200 }}
              showSearch optionFilterProp="label"
              allowClear placeholder="All weeks"
            >
              {weekOptions.map((opt) => (
                <Option key={opt.value} value={opt.value} label={opt.label}>
                  <span style={{ color: weeksWithTasks.has(opt.value) ? "#0f172a" : "#94a3b8", fontWeight: weeksWithTasks.has(opt.value) ? 700 : 400 }}>
                    {opt.label} {weeksWithTasks.has(opt.value) ? "●" : ""}
                  </span>
                </Option>
              ))}
            </Select>
          </div>
          {/* User filter */}
          {userOptions.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", borderRadius: 8, border: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
              <Select
                value={selectedUserId}
                onChange={(val) => setSelectedUserId(val ?? null)}
                size="small" bordered={false} style={{ width: 180 }}
                showSearch allowClear placeholder="All users"
                optionFilterProp="label"
                loading={positionsLoading}
              >
                {userOptions.map((u) => (
                  <Option key={u.userId} value={u.userId} label={u.name}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {u.imageUrl ? (
                        <img src={u.imageUrl} alt={u.name} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {u.name.split(" ").filter(Boolean).map(n => n[0].toUpperCase()).slice(0, 2).join("")}
                        </div>
                      )}
                      <span style={{ fontSize: 12 }}>{u.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>
          )}
          {/* Unit filter */}
          {unitOptions.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 14px", borderRadius: 8, border: "1px solid #e2e8f0", backgroundColor: "#fff" }}>
              <Select
                value={selectedUnit}
                onChange={(val) => setSelectedUnit(val ?? null)}
                size="small" bordered={false} style={{ width: 160 }}
                showSearch allowClear placeholder="All units"
                optionFilterProp="children"
              >
                {unitOptions.map((u) => (
                  <Option key={u} value={u}>{u}</Option>
                ))}
              </Select>
            </div>
          )}
          {/* Completion counter */}
          <div style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #a7f3d0", backgroundColor: "#ecfdf5", fontSize: 12, fontWeight: 700, color: "#065f46" }}>
            {completedCount}/{weekTasks.length} Completed ({weekTasks.length > 0 ? Math.round((completedCount / weekTasks.length) * 100) : 0}%)
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{ backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, padding: "10px 16px", marginBottom: 20, fontSize: 12, color: "#065f46", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#059669" }} />
        Please mark each assigned task as <strong style={{ margin: "0 3px" }}>Completed</strong> or <span style={{ color: "#dc2626", fontWeight: 600, margin: "0 3px" }}>Not completed</span>. Mandatory reason tags required for any incomplete task to maintain C-suite alignment.
      </div>

      {weekTasks.length === 0 ? (
        <div className="flex justify-center items-center" style={{ minHeight: 250 }}>
          <div className="text-center">
            <div className="text-3xl text-gray-400"><WarningOutlined /></div>
            <div className="text-lg pri-font text-gray-400 mt-2">No tasks for this week</div>
            <div className="text-gray-400 text-sm">No tasks are due or active in Week {selectedWeek}</div>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }} className="weekly-reflection-grid">
          <style>{`
            @media (max-width: 1100px) { .weekly-reflection-grid { grid-template-columns: repeat(2, 1fr) !important; } }
            @media (max-width: 700px) { .weekly-reflection-grid { grid-template-columns: 1fr !important; } }
          `}</style>
          {(() => {
            // Group tasks by plan (split plan → parent plan hierarchy)
            const planMap = new Map();
            sortedTasks.forEach(task => {
              const planId = task.planId || "unknown";
              const planTitle = task.plan?.title || "Unassigned Plan";
              const parentTitle = task.parentPlanTitle || null;
              const key = parentTitle ? `${parentTitle}|||${planTitle}` : planTitle;
              if (!planMap.has(key)) planMap.set(key, { parentTitle, planTitle, tasks: [] });
              planMap.get(key).tasks.push(task);
            });

            return Array.from(planMap.entries()).map(([key, group]) => {
              const isExpanded = expandedPlans[key] !== false; // default open
              return (
                <div key={key} style={{ gridColumn: "1 / -1", marginBottom: 8 }}>
                  {group.parentTitle && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 2 }}>
                      {group.parentTitle}
                    </div>
                  )}
                  <div
                    onClick={() => setExpandedPlans(prev => ({ ...prev, [key]: !isExpanded }))}
                    style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, paddingLeft: group.parentTitle ? 12 : 0 }}
                  >
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>{isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}</span>
                    {group.planTitle}
                  </div>
                  {isExpanded && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, paddingLeft: group.parentTitle ? 12 : 0 }} className="weekly-reflection-grid">
                      {group.tasks.map((task) => {
                        const localStatus = statuses[task.id];
                    const isCompleted = task.completionStatus || localStatus === "completed";
            const isNotCompleted = localStatus === "not_completed";

            // Card styling based on state
            let cardBg = "#ffffff";
            let cardBorder = "1px solid #e2e8f0";
            let cardShadow = "none";
            const isOnHold = task.onHold;
            if (isOnHold && !isCompleted) {
              cardBg = "#fffbeb";
              cardBorder = "2px solid #fbbf24";
              cardShadow = "0 2px 8px rgba(251,191,36,0.15)";
            } else if (isCompleted) {
              cardBg = "#f0fdf4";
              cardBorder = "1px solid #86efac";
              cardShadow = "0 1px 3px rgba(34,197,94,0.1)";
            } else if (isNotCompleted) {
              cardBg = "#fef2f2";
              cardBorder = "1px solid #fecaca";
              cardShadow = "0 1px 3px rgba(239,68,68,0.08)";
            } else {
              // pending — light red border
              cardBg = "#ffffff";
              cardBorder = "1px solid #fca5a5";
              cardShadow = "none";
            }

            const assignedName = task.positions?.users
              ? `${task.positions.users.firstName || ""} ${task.positions.users.lastName || ""}`.trim()
              : null;
            const imageUrl = task.positions?.users?.imageUrl || null;
            const userInitials = assignedName
              ? assignedName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
              : null;
            const designation = task.positions?.designations?.name || task.positions?.designations?.abbreviation || "";

            return (
              <div
                key={task.id}
                style={{
                  border: cardBorder,
                  borderRadius: 12,
                  padding: "18px 20px",
                  backgroundColor: cardBg,
                  boxShadow: cardShadow,
                  transition: "all 0.2s ease, transform 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.06)"; if (isOnHold && !isCompleted) e.currentTarget.style.borderColor = "#fbbf24"; else if (isCompleted) e.currentTarget.style.borderColor = "#86efac"; /* pending keeps its red border */ }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = cardShadow; e.currentTarget.style.borderColor = isOnHold && !isCompleted ? "#fbbf24" : isCompleted ? "#86efac" : isNotCompleted ? "#fecaca" : "#fca5a5"; }}
              >
                {/* Top row: task name + avatar */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", lineHeight: 1.4 }}>
                      {task.name}
                    </div>
                  </div>
                  {/* Profile avatar */}
                  {assignedName && (
                    imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={assignedName}
                        style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: "2px solid #fff", boxShadow: "0 0 0 1px #e2e8f0", flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "monospace", flexShrink: 0, border: "2px solid #fff", boxShadow: "0 0 0 1px #e2e8f0" }}>
                        {userInitials}
                      </div>
                    )
                  )}
                </div>

                {/* Status tags row */}
                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  {isOnHold && !isCompleted && (
                    <span style={{ fontSize: 10, backgroundColor: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", border: "1px solid #fde68a" }}>
                      On Hold
                    </span>
                  )}
                  {isCompleted && (
                    <span style={{ fontSize: 10, backgroundColor: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", border: "1px solid #86efac" }}>
                      Completed
                    </span>
                  )}
                </div>

                {/* Meta: target date + user */}
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                  {task.taskStartDate && (
                    <span>Start: <span style={{ color: "#334155", fontWeight: 600 }}>{moment(task.taskStartDate).format("DD  MMM  YYYY")}</span></span>
                  )}
                  {task.taskStartDate && task.taskEndDate && <span> &nbsp;→&nbsp; </span>}
                  {task.taskEndDate && (
                    <span>Target: <span style={{ color: "#334155", fontWeight: 600 }}>{moment(task.taskEndDate).format("DD  MMM  YYYY")}</span></span>
                  )}
                  {assignedName && (
                    <div style={{ marginTop: 2, fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", color: "#94a3b8", fontSize: 11 }}>
                      {assignedName} {designation ? `(${designation})` : ""}
                    </div>
                  )}
                </div>

                {/* Action buttons — single button state when decided */}
                <div style={{ marginTop: "auto" }}>
                  {isCompleted ? (
                    // Completed state: single full-width emerald bar
                    <div style={{ display: "flex", alignItems: "center", borderRadius: 6, overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 12px", backgroundColor: "#059669", color: "#fff", fontSize: 12, fontWeight: 700 }}>
                        <CheckOutlined style={{ fontSize: 13, strokeWidth: 3 }} /> Completed
                      </div>
                      {!task.completionStatus && (
                        <div
                          onClick={() => handleUndo(task.id)}
                          style={{ padding: "8px 12px", backgroundColor: "#047857", color: "#a7f3d0", fontSize: 11, fontWeight: 600, cursor: "pointer", borderLeft: "1px solid #065f46" }}
                        >
                          Undo
                        </div>
                      )}
                    </div>
                  ) : isOnHold ? (
                    // On hold state: white bg, amber border, just says On Hold — not clickable
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "8px 12px", borderRadius: 6,
                      backgroundColor: "#fff", border: "1px solid #fbbf24",
                      color: "#92400e", fontSize: 12, fontWeight: 600,
                    }}>
                      On Hold
                    </div>
                  ) : (
    // Pending — clean neutral button, hover smoothly reveals "Mark completed"
                    <PendingButton onConfirm={() => {
                      Modal.confirm({
                        title: "Mark task as completed?",
                        content: `Are you sure you want to mark "${task.name}" as completed?`,
                        okText: "Mark completed",
                        cancelText: "Cancel",
                        onOk: () => { handleStatusChange(task.id, "completed"); handleComplete(task.id); },
                      });
                    }} />
                  )}
                </div>
              </div>
            );
          })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
